import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Table, Tag, Space, Typography, Avatar, Tooltip, Skeleton, Flex, Grid, message, Pagination } from 'antd';
import {
    UserOutlined,
    RobotOutlined,
    CommentOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { deleteTicket, getTickets, updateTicket } from '../TicketServices';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocket from '../../../Hooks/useSocket';
import useVisualHighlight from '../../../Hooks/useVisualHighlight';
import { Resource, Action } from '../../../Constants/socket';
import SkeletonTable from './SkeletonTable';
import AppTable from '../../Shared/AppTable/AppTable';
import RowActions from '../../Shared/RowActions/RowActions';
import { resolveBackendMessage } from '../../../Utils/apiMessages';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const STATUS_COLORS = {
    open: 'blue',
    in_progress: 'orange',
    resolved: 'green',
    closed: 'default',
};

const STATUS_DOT_COLORS = {
    open: '#1677ff',
    in_progress: '#fa8c16',
    resolved: '#52c41a',
    closed: '#8c8c8c',
};

const PRIORITY_COLORS = {
    low: 'default',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
};

const CATEGORY_COLORS = {
    escalation: 'volcano',
    reclamation: 'magenta',
    suggestion: 'cyan',
    order: 'geekblue',
    other: 'default',
};

const TICKET_STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

/**
 * Returns a human-friendly relative time string.
 */
const timeAgo = (dateString) => {
    if (!dateString) return '';
    try {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays < 30) return `${diffDays}d`;
        return `${Math.floor(diffDays / 30)}mo`;
    } catch {
        return '';
    }
};

/* ─── Mobile Card ─── */
const TicketCard = ({ ticket, isDarkMode, t, onClick, highlightClass }) => (
    <div
        className={`ticket-list-card ${isDarkMode ? 'card-dark' : 'card-light'} ${highlightClass}`}
        onClick={onClick}
    >
        {/* Priority bar */}
        {ticket.priority && (
            <div
                className="card-priority-indicator"
                style={{
                    backgroundColor:
                        ticket.priority === 'urgent' ? '#ff4d4f' :
                            ticket.priority === 'high' ? '#fa8c16' :
                                ticket.priority === 'medium' ? '#1677ff' : '#8c8c8c',
                }}
            />
        )}

        {/* Top row: ID + Time */}
        <Flex align="center" justify="space-between" style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, opacity: 0.5 }}>
                {ticket.id ? `#${ticket.id}` : ''}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
                {timeAgo(ticket.created_at)}
            </Text>
        </Flex>

        {/* Subject */}
        <Text
            strong
            style={{
                fontSize: 13,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginBottom: 8,
            }}
        >
            {ticket.subject || t('no_subject')}
        </Text>

        {/* Tags row */}
        <Flex wrap="wrap" gap={4} style={{ marginBottom: 8 }}>
            <Tag color={STATUS_COLORS[ticket.status]} style={{ margin: 0, borderRadius: 4, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
                <span style={{
                    display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                    backgroundColor: STATUS_DOT_COLORS[ticket.status], marginInlineEnd: 4, verticalAlign: 'middle',
                }} />
                {t(`ticket_status_${ticket.status}`)}
            </Tag>
            {ticket.priority && (
                <Tag color={PRIORITY_COLORS[ticket.priority]} style={{ margin: 0, borderRadius: 4, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
                    {t(`ticket_priority_${ticket.priority}`)}
                </Tag>
            )}
            {ticket.category && (
                <Tag color={CATEGORY_COLORS[ticket.category]} style={{ margin: 0, borderRadius: 4, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
                    {t(`ticket_category_${ticket.category}`)}
                </Tag>
            )}
        </Flex>

        {/* Footer: Contact + Comments */}
        <Flex align="center" justify="space-between" style={{ opacity: 0.6, fontSize: 11 }}>
            <Space size={6}>
                {ticket.contact ? (
                    <>
                        <Avatar
                            size={18}
                            src={ticket.contact.picture_url}
                            icon={<UserOutlined />}
                            style={{
                                background: isDarkMode ? '#264f78' : '#e6f4ff',
                                color: isDarkMode ? '#69b1ff' : '#1677ff',
                                fontSize: 9,
                            }}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>{ticket.contact.full_name}</Text>
                    </>
                ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {ticket.opened_by_type === 'agent' ? <RobotOutlined /> : <UserOutlined />}
                    </Text>
                )}
            </Space>
            {ticket.comment_count > 0 && (
                <Space size={3}>
                    <CommentOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary" style={{ fontSize: 11 }}>{ticket.comment_count}</Text>
                </Space>
            )}
        </Flex>
    </div>
);

/* ─── Main Component ─── */
const TicketTableView = ({ search, filters, sortBy, sortDirection, refreshKey, forceCompact = false, onDataLoaded, onTicketClick }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint();
    const isCompact = forceCompact || !screens.md;
    const { handleError } = useErrorHandler();
    const { socket } = useSocket();
    
    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();
    
    const [tickets, setTickets] = useState([]);
    const [skeleton, setSkeleton] = useState(true);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);

    const fetchTableTickets = useCallback(async (currentPage, size) => {
        try {
            setLoading(true);
            const response = await getTickets({
                page: currentPage,
                size: size,
                search,
                filters,
                sort_by: sortBy,
                sort_direction: sortDirection,
            });
            setTickets(response?.data || []);
            const newTotal = response?.metadata?.total || 0;
            setTotal(newTotal);
            if (onDataLoaded) onDataLoaded(newTotal);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            setSkeleton(false);
        }
    }, [search, filters, sortBy, sortDirection, handleError]);

    useEffect(() => {
        fetchTableTickets(1, pageSize);
        setPage(1);
    }, [fetchTableTickets, refreshKey]);

    // Memoize ticket IDs for socket subscription
    const ticketIds = useMemo(() =>
        tickets.map(ticket => ticket.id).filter(Boolean)
    , [tickets]);

    // Subscribe to individual ticket IDs for UPDATE/DELETE events
    useSocketSubscription(Resource.TICKET, ticketIds.length > 0 ? ticketIds : null);

    const handlePageChange = (newPage, newSize) => {
        setPage(newPage);
        setPageSize(newSize);
        fetchTableTickets(newPage, newSize);
    };

    const handleTicketEvent = useCallback((data) => {
        fetchTableTickets(page, pageSize);

        // Subscribe to new ticket from server_datetime to cover the readiness gap
        if (data?.id && data?.server_datetime && socket) {
            socket.subscribeToResource(Resource.TICKET, [data.id], data.server_datetime);
        }
        if (data?.id) highlightRow(data.id);
    }, [fetchTableTickets, page, pageSize, socket, highlightRow]);

    const handleTicketUpdated = useCallback((data) => {
        setTickets(prev =>
            prev.map(ticket => ticket.id === data.id ? { ...ticket, ...data } : ticket)
        );
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [highlightRow]);

    useSocketEvent(Resource.TICKET, Action.CREATE, handleTicketEvent);
    useSocketEvent(Resource.TICKET, Action.UPDATE, handleTicketUpdated);
    useSocketEvent(Resource.TICKET, Action.DELETE, handleTicketEvent);

    const openTicket = useCallback((ticket) => {
        if (onTicketClick) {
            onTicketClick(ticket);
        } else {
            navigate(`/tickets/${ticket.id}`);
        }
    }, [navigate, onTicketClick]);

    const handleQuickStatusChange = useCallback(async (ticket, status) => {
        if (!ticket?.id || ticket.status === status) return;

        try {
            setLoading(true);
            const payload = await updateTicket(ticket.id, { status });
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            await fetchTableTickets(page, pageSize);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [fetchTableTickets, handleError, page, pageSize, t]);

    const handleQuickDelete = useCallback(async (ticket) => {
        if (!ticket?.id) return;

        try {
            setLoading(true);
            const payload = await deleteTicket(ticket.id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            await fetchTableTickets(page, pageSize);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [fetchTableTickets, handleError, page, pageSize, t]);

    const columns = useMemo(() => [
        {
            title: t('ticket_subject'),
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true,
            render: (text, record) => (
                <div style={{ minWidth: 0 }}>
                    <Text
                        type="secondary"
                        style={{
                            display: 'block',
                            fontFamily: 'monospace',
                            fontSize: 11,
                            fontWeight: 600,
                            marginBottom: 2,
                        }}
                    >
                        {record.id ? `#${record.id}` : ''}
                    </Text>
                    <Text
                        strong
                        style={{
                            fontSize: 13,
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            cursor: 'pointer',
                        }}
                    >
                        {text || t('no_subject')}
                    </Text>
                    {record.description && record.description !== text && (
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 11,
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                marginTop: 1,
                            }}
                        >
                            {record.description}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: t('ticket_status'),
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (status) => (
                <Tag color={STATUS_COLORS[status]} style={{ margin: 0, borderRadius: 4, fontWeight: 500 }}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: STATUS_DOT_COLORS[status],
                            marginInlineEnd: 5,
                            verticalAlign: 'middle',
                        }}
                    />
                    {t(`ticket_status_${status}`)}
                </Tag>
            ),
            filters: TICKET_STATUSES.map(s => ({
                text: t(`ticket_status_${s}`),
                value: s,
            })),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: t('ticket_priority'),
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (priority) => priority ? (
                <Tag color={PRIORITY_COLORS[priority]} style={{ margin: 0, borderRadius: 4 }}>
                    {t(`ticket_priority_${priority}`)}
                </Tag>
            ) : null,
        },
        {
            title: t('ticket_category'),
            dataIndex: 'category',
            key: 'category',
            width: 120,
            responsive: ['md'],
            render: (category) => category ? (
                <Tag color={CATEGORY_COLORS[category]} style={{ margin: 0, borderRadius: 4 }}>
                    {t(`ticket_category_${category}`)}
                </Tag>
            ) : null,
        },
        {
            title: t('contact'),
            dataIndex: 'contact',
            key: 'contact',
            width: 150,
            responsive: ['lg'],
            render: (contact) => contact ? (
                <Space size={6}>
                    <Avatar
                        size={22}
                        src={contact.picture_url}
                        icon={<UserOutlined />}
                        style={{
                            background: isDarkMode ? '#264f78' : '#e6f4ff',
                            color: isDarkMode ? '#69b1ff' : '#1677ff',
                            fontSize: 11,
                        }}
                    />
                    <Text style={{ fontSize: 12 }}>{contact.full_name}</Text>
                </Space>
            ) : null,
        },
        {
            title: <CommentOutlined />,
            dataIndex: 'comment_count',
            key: 'comments',
            width: 55,
            align: 'center',
            responsive: ['md'],
            render: (count) => count > 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>{count}</Text>
            ) : null,
        },
        {
            title: <ClockCircleOutlined />,
            dataIndex: 'created_at',
            key: 'created_at',
            width: 70,
            align: 'center',
            render: (date) => (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {timeAgo(date)}
                </Text>
            ),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            defaultSortOrder: 'descend',
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 96,
            align: 'center',
            render: (_, record) => (
                <div onClick={(event) => event.stopPropagation()}>
                    <RowActions
                        screens={screens}
                        primary={{
                            label: t('view_ticket'),
                            tooltip: t('view_ticket'),
                            icon: <EyeOutlined />,
                            onClick: () => openTicket(record),
                        }}
                        items={[
                            ...TICKET_STATUSES
                                .filter(status => status !== record.status)
                                .map(status => ({
                                    key: `status-${status}`,
                                    label: t(`ticket_status_${status}`),
                                    icon: (
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: STATUS_DOT_COLORS[status],
                                            }}
                                        />
                                    ),
                                    onClick: () => handleQuickStatusChange(record, status),
                                })),
                            {
                                key: 'delete',
                                label: t('delete'),
                                icon: <DeleteOutlined />,
                                danger: true,
                                onClick: () => handleQuickDelete(record),
                                confirm: {
                                    title: t('delete_ticket_confirmation'),
                                    okText: t('delete'),
                                    cancelText: t('cancel'),
                                },
                            },
                        ]}
                    />
                </div>
            ),
        },
    ], [t, isDarkMode, screens, openTicket, handleQuickStatusChange, handleQuickDelete]);

    if (skeleton) {
        if (isCompact) {
            return (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                        <Skeleton key={i} active paragraph={{ rows: 2 }} />
                    ))}
                </div>
            );
        }
        return <SkeletonTable columns={columns} rowCount={10} />;
    }

    /* ─── Mobile: Card List ─── */
    if (isCompact) {
        return (
            <Flex vertical style={{ height: '100%', overflow: 'hidden' }}>
                <div className="ticket-list-mobile" style={{ height: 'auto', flex: 1, paddingBottom: 12 }}>
                    {tickets.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                            <Text type="secondary">{t('no_tickets')}</Text>
                        </div>
                    ) : (
                        tickets.map((ticket) => (
                            <TicketCard
                                key={ticket.id}
                                ticket={ticket}
                                isDarkMode={isDarkMode}
                                t={t}
                                onClick={() => onTicketClick ? onTicketClick(ticket) : navigate(`/tickets/${ticket.id}`)}
                                highlightClass={getHighlightClass ? getHighlightClass(ticket.id, 'socket-highlight-target-card') : ''}
                            />
                        ))
                    )}
                </div>
                {total > 0 && (
                    <Flex justify="center" style={{ padding: '12px 0', borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
                        <Pagination
                            size="small"
                            simple
                            current={page}
                            pageSize={pageSize}
                            total={total}
                            onChange={handlePageChange}
                        />
                    </Flex>
                )}
            </Flex>
        );
    }

    /* ─── Desktop: Table ─── */
    return (
        <AppTable
            dataSource={tickets}
            columns={columns}
            rowKey="id"
            rowClassName={(record) => getHighlightClass(record.id)}
            size="small"
            tableLayout="fixed"
            loading={loading}
            pagination={{
                current: page,
                pageSize,
                total,
                onChange: handlePageChange,
            }}
            scroll={{ x: undefined, y: 'calc(100vh - 280px)' }}
            style={{ borderRadius: 8, overflow: 'hidden' }}
            onRow={(record) => ({
                onClick: () => openTicket(record),
                style: { cursor: 'pointer' },
            })}
            locale={{
                emptyText: t('no_tickets'),
            }}
        />
    );
};

export default TicketTableView;
